import React, { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2, ClipboardCheck, Eye, FileSignature, History, Link2, PenLine, RotateCcw, Send, Undo2, XCircle,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardFooter, AppModal, Callout, EmptyState, FormField, Input, StatusBadge,
  StepRail, Switch, Textarea,
} from "../../../ui";
import Shimmer from "../../../Common/Shimmer";
import RecordCardHead from "../RecordCardHead";
import useEvaluation, { fetchPdfBytes } from "../../profile/useEvaluation";
import useProfileOptions from "../../profile/useProfileOptions";
import EvaluationForm, { bodyFromForm, formFromEvaluation } from "../../profile/EvaluationForm";
import AgreementPdfModal from "../../profile/AgreementPdfModal";
import SignatureCapture from "../../profile/SignatureCapture";
import { EVALUATION_STEPS, documentCounts, stepOf } from "../../profile/studentProfileRules";
import { formatDate, formatDateTimeFriendly } from "../../../../utils/format";
import { getCurrentUser } from "../../../../utils/auth";

const ACTION_WORDS = {
  submitted: "Submitted", resubmitted: "Submitted again", return: "Returned to the student", approve: "Approved — agreement drafted",
  send: "Sent to the student", withdraw: "Withdrawn", void: "Voided", student_signed: "Signed by the student", completed: "Signed by the institute",
  details_verified: "Details marked verified", details_unverified: "Details verification removed",
};

/** A dialog that asks for one sentence before a move. */
const NoteModal = ({ show, onHide, title, label, hint, confirmText, variant = "default", onConfirm, busy }) => {
  const [note, setNote] = useState("");
  useEffect(() => { if (show) setNote(""); }, [show]);
  return (
    <AppModal
      show={show}
      onHide={onHide}
      title={title}
      size="sm"
      footer={(
        <>
          <AppButton variant="outline" onClick={onHide} disabled={busy}>Cancel</AppButton>
          <AppButton variant={variant} onClick={() => onConfirm(note.trim())} disabled={!note.trim() || busy}>{confirmText}</AppButton>
        </>
      )}
    >
      <FormField label={label} hint={hint} required>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
      </FormField>
    </AppModal>
  );
};

/** The institute's signature: who signs, and the signature itself. */
const SignModal = ({ show, onHide, onConfirm, busy, defaultName, defaultTitle }) => {
  const [name, setName] = useState(defaultName || "");
  const [title, setTitle] = useState(defaultTitle || "");
  const [signature, setSignature] = useState(null);
  useEffect(() => { if (show) { setName(defaultName || ""); setTitle(defaultTitle || ""); setSignature(null); } }, [show, defaultName, defaultTitle]);
  return (
    <AppModal
      show={show}
      onHide={onHide}
      title="Sign for the institute"
      size="md"
      footer={(
        <>
          <AppButton variant="outline" onClick={onHide} disabled={busy}>Cancel</AppButton>
          <AppButton onClick={() => onConfirm({ signature, signatoryName: name.trim(), signatoryTitle: title.trim() })} disabled={!signature || !name.trim() || busy}>
            <PenLine size={16} /> {busy ? "Signing…" : "Sign and lock"}
          </AppButton>
        </>
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Callout tone="warning">Once signed, the agreement is locked for both sides and the student receives their copy by email.</Callout>
        <div className="tw-grid tw-gap-3 min-[576px]:tw-grid-cols-2">
          <FormField label="Signatory's name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <FormField label="Designation">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Director — Placements" />
          </FormField>
        </div>
        <SignatureCapture onChange={setSignature} disabled={busy} />
      </div>
    </AppModal>
  );
};

/**
 * EvaluationTab — a contract student's pass through review, the report and the
 * agreement, on the admin record.
 *
 * Props:
 *   studentId, student
 *   docs         — useStudentDocuments(staffDocumentsApi(id)); the verification card counts them
 *   canEvaluate  — may verify, write the report, approve, send, withdraw, return, void
 *   canSign      — may sign for the institute
 *   onOpenTab    — (value) => void, to jump to Documents
 *   onNotify     — (message, type) => void
 *   onSendLink   — emails the student their no-login profile link; omitted when not allowed
 */
const EvaluationTab = ({ studentId, student, docs, canEvaluate, canSign, onOpenTab, onNotify, onSendLink }) => {
  const ev = useEvaluation(studentId);
  const { options } = useProfileOptions();
  const me = getCurrentUser();
  const [form, setForm] = useState(() => formFromEvaluation(null, { counsellorName: me?.name }));
  const [dirty, setDirty] = useState(false);
  const [modal, setModal] = useState(null); // 'return' | 'void' | 'sign' | 'pdf'
  const [pdfKey, setPdfKey] = useState(0);

  const evaluation = ev.evaluation;
  const status = evaluation?.status;
  const fetchPdf = useCallback(() => fetchPdfBytes(ev.pdfUrl), [ev.pdfUrl]);

  // The form follows the server's copy until the admin starts typing.
  useEffect(() => {
    if (!dirty) setForm(formFromEvaluation(evaluation, { counsellorName: me?.name }));
  }, [evaluation, dirty, me?.name]);

  const change = (next) => { setForm(next); setDirty(true); };
  const counts = documentCounts(docs?.documents || []);
  const editable = !!evaluation?.editable && canEvaluate;

  const run = async (fn, done) => {
    try {
      await fn();
      setDirty(false);
      setPdfKey((k) => k + 1);
      docs?.reload?.();
      if (done) onNotify?.(done);
    } catch (err) {
      onNotify?.(err.problems?.length ? err.problems.join(" ") : err.message, "error");
    }
  };

  const save = () => run(() => ev.save(bodyFromForm(form)), "Evaluation saved.");
  const approve = () => run(async () => {
    if (dirty) await ev.save(bodyFromForm(form));
    await ev.act("approve");
  }, "Approved. The agreement is drafted — preview it, then send it to the student.");
  const send = () => run(() => ev.act("send"), `Agreement sent to ${student.email}.`);
  const withdraw = () => run(() => ev.act("withdraw"), "Agreement withdrawn. The report can be edited again.");
  const returnToStudent = (note) => run(async () => { await ev.act("return", note); setModal(null); }, "Returned to the student with your note.");
  const voidIt = (note) => run(async () => { await ev.act("void", note); setModal(null); }, "Agreement voided.");
  const sign = (body) => run(async () => { await ev.sign(body); setModal(null); }, "Signed. The agreement is complete and the student has their copy.");
  const start = () => run(() => ev.submit(), "Evaluation started — verify the details and documents, then write the report.");

  if (ev.loading && !evaluation) return <Shimmer height={120} count={3} />;

  if (!evaluation) {
    return (
      <EmptyState
        icon={FileSignature}
        title="No application yet"
        description={canEvaluate
          ? `${student.name} has not submitted their profile for review. Email them the link to fill it in — no login needed — or start the evaluation on their behalf.`
          : `${student.name} has not submitted their profile for review.`}
        action={(canEvaluate || onSendLink) && (
          <span className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
            {onSendLink && (
              <AppButton onClick={onSendLink}><Link2 size={16} /> Send evaluation link</AppButton>
            )}
            {canEvaluate && (
              <AppButton variant={onSendLink ? "outline" : "default"} onClick={start} disabled={ev.busy === "submit"}>
                <ClipboardCheck size={16} /> Start the evaluation
              </AppButton>
            )}
          </span>
        )}
      />
    );
  }

  const a = evaluation.agreement || {};
  const problems = evaluation.approvalProblems || [];
  const canApprove = ["submitted", "returned"].includes(status);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <StatusBadge status={status} domain="studentEvaluation" />
            <span className="tw-text-sm tw-text-content-muted">
              Submitted {formatDate(evaluation.submittedAt)}{evaluation.submittedBy === "staff" ? " by the office" : ""}
              {a.number ? ` · ${a.number}` : ""}
            </span>
          </div>
          <StepRail steps={EVALUATION_STEPS} current={stepOf(status)} reached={stepOf(status)} variant="bar" />
          {status === "returned" && <Callout tone="warning">Returned to the student: {evaluation.returnNote}</Callout>}
          {status === "void" && <Callout tone="info">Voided: {evaluation.voidReason}</Callout>}
          {ev.error && <Callout tone="danger" role="alert">{ev.error}</Callout>}
        </AppCardContent>
      </AppCard>

      <div className="tw-grid tw-items-start tw-gap-4 min-[1200px]:tw-grid-cols-3">
        <div className="tw-flex tw-flex-col tw-gap-4 min-[1200px]:tw-col-span-2">
          <AppCard>
            <RecordCardHead
              title="Verification"
              description="Every document checked, and the details confirmed, before the report is approved."
              action={<AppButton size="sm" variant="ghost" onClick={() => onOpenTab("documents")}>Documents →</AppButton>}
            />
            <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
              <p className="tw-m-0 tw-text-sm tw-text-content">
                {counts.total === 0 ? "No documents uploaded yet." : (
                  <>
                    {counts.total} document{counts.total === 1 ? "" : "s"} · <span className="tw-text-success-fg">{counts.verified} verified</span>
                    {counts.pending ? <> · <span className="tw-text-warning-fg">{counts.pending} awaiting check</span></> : null}
                    {counts.rejected ? <> · <span className="tw-text-danger-fg">{counts.rejected} rejected</span></> : null}
                  </>
                )}
              </p>
              <Switch
                label="Student details verified"
                description={evaluation.verification?.detailsVerifiedAt
                  ? `Confirmed by ${evaluation.verification.detailsVerifiedByName || "the office"} on ${formatDate(evaluation.verification.detailsVerifiedAt)}.`
                  : "Name, contact, education and experience checked against the documents."}
                checked={!!evaluation.verification?.detailsVerifiedAt}
                onChange={(next) => run(() => ev.verifyDetails(next))}
                disabled={!editable || ev.busy === "verify"}
              />
            </AppCardContent>
            {canEvaluate && ["submitted", "evaluated"].includes(status) && (
              <AppCardFooter className="tw-flex tw-justify-end">
                <AppButton size="sm" variant="outline" onClick={() => setModal("return")}>
                  <Undo2 size={16} /> Return to student
                </AppButton>
              </AppCardFooter>
            )}
          </AppCard>

          <AppCard>
            <RecordCardHead
              title="Evaluation report"
              description={editable ? "Kept short: the recommendation, the indicative figures, the programme." : "Read-only — the agreement has been sent."}
            />
            <AppCardContent>
              <EvaluationForm value={form} onChange={change} options={options} readOnly={!editable} />
            </AppCardContent>
            {editable && (
              <AppCardFooter className="tw-flex tw-flex-col tw-gap-3">
                {canApprove && problems.length > 0 && (
                  <Callout tone="info">
                    Before approving: {problems.join(" ")}
                  </Callout>
                )}
                <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-2">
                  <AppButton variant="outline" size="sm" onClick={save} disabled={!dirty || ev.busy === "save"}>
                    {ev.busy === "save" ? "Saving…" : "Save draft"}
                  </AppButton>
                  {canApprove && (
                    <AppButton size="sm" onClick={approve} disabled={ev.busy === "approve" || ev.busy === "save"}>
                      <CheckCircle2 size={16} /> {ev.busy === "approve" ? "Approving…" : "Approve & draft agreement"}
                    </AppButton>
                  )}
                </div>
              </AppCardFooter>
            )}
          </AppCard>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-4">
          <AppCard>
            <RecordCardHead title="Agreement" description={a.number ? `No. ${a.number}` : "Drafted when the report is approved."} />
            <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
              {a.hasPdf ? (
                <>
                  <dl className="tw-m-0 tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-3 tw-gap-y-1 tw-text-[0.8125rem]">
                    <dt className="tw-text-content-muted">Sent</dt><dd className="tw-m-0 tw-text-content">{a.sentAt ? formatDateTimeFriendly(a.sentAt) : "Not yet"}</dd>
                    <dt className="tw-text-content-muted">Student signed</dt><dd className="tw-m-0 tw-text-content">{a.studentSignedAt ? formatDateTimeFriendly(a.studentSignedAt) : "Not yet"}</dd>
                    <dt className="tw-text-content-muted">Institute signed</dt><dd className="tw-m-0 tw-text-content">{a.instituteSignedAt ? `${formatDateTimeFriendly(a.instituteSignedAt)}${a.signatoryName ? ` · ${a.signatoryName}` : ""}` : "Not yet"}</dd>
                  </dl>
                  <AppButton variant="outline" size="sm" onClick={() => setModal("pdf")}>
                    <Eye size={16} /> {status === "completed" ? "Open signed agreement" : status === "evaluated" ? "Preview draft" : "Open agreement"}
                  </AppButton>
                  {canEvaluate && status === "evaluated" && (
                    <AppButton size="sm" onClick={send} disabled={ev.busy === "send" || !student.email}>
                      <Send size={16} /> {ev.busy === "send" ? "Sending…" : "Send to student"}
                    </AppButton>
                  )}
                  {canEvaluate && status === "evaluated" && !student.email && (
                    <Callout tone="warning">The student has no email address. Add one — the agreement is acknowledged with a code sent by email.</Callout>
                  )}
                  {canEvaluate && status === "sent" && (
                    <>
                      <Callout tone="info">Waiting for {student.name} to read, acknowledge and sign from My Profile.</Callout>
                      <AppButton variant="outline" size="sm" onClick={withdraw} disabled={ev.busy === "withdraw"}>
                        <RotateCcw size={16} /> Withdraw and edit
                      </AppButton>
                    </>
                  )}
                  {status === "student_signed" && (
                    canSign ? (
                      <AppButton size="sm" onClick={() => setModal("sign")}>
                        <PenLine size={16} /> Sign for the institute
                      </AppButton>
                    ) : (
                      <Callout tone="info">The student has signed. Someone with General Settings signs for the institute.</Callout>
                    )
                  )}
                  {status === "completed" && a.sha256 && (
                    <p className="tw-m-0 tw-break-all tw-font-mono tw-text-[0.6875rem] tw-text-content-subtle">SHA-256 {a.sha256}</p>
                  )}
                </>
              ) : (
                <p className="tw-m-0 tw-text-sm tw-text-content-subtle">
                  {status === "void" ? "This evaluation was voided." : "Approve the report to draft the agreement."}
                </p>
              )}
              {canEvaluate && ["submitted", "returned", "evaluated", "sent", "student_signed"].includes(status) && (
                <AppButton variant="ghost-destructive" size="sm" onClick={() => setModal("void")} className="tw-self-start">
                  <XCircle size={16} /> Void
                </AppButton>
              )}
              {canEvaluate && ["completed", "void"].includes(status) && (
                <AppButton variant="outline" size="sm" onClick={start} disabled={ev.busy === "submit"}>
                  <ClipboardCheck size={16} /> Start a new evaluation
                </AppButton>
              )}
            </AppCardContent>
          </AppCard>

          <AppCard>
            <RecordCardHead title="History" />
            <AppCardContent>
              {(evaluation.history || []).length === 0 ? (
                <p className="tw-m-0 tw-text-sm tw-text-content-subtle">Nothing yet.</p>
              ) : (
                <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
                  {[...evaluation.history].reverse().slice(0, 10).map((h, i) => (
                    <li key={`${h.at}-${i}`} className="tw-flex tw-gap-2 tw-text-[0.8125rem]">
                      <History size={14} className="tw-mt-0.5 tw-shrink-0 tw-text-content-subtle" aria-hidden="true" />
                      <span className="tw-min-w-0">
                        <span className="tw-text-content">{ACTION_WORDS[h.action] || h.action}</span>
                        <span className="tw-text-content-subtle"> · {formatDateTimeFriendly(h.at)}{h.byName ? ` · ${h.byName}` : ""}</span>
                        {h.note && <span className="tw-block tw-text-content-muted">“{h.note}”</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </AppCardContent>
          </AppCard>
        </div>
      </div>

      <NoteModal
        show={modal === "return"}
        onHide={() => setModal(null)}
        title="Return to the student"
        label="What should they change?"
        hint="The student reads this on My Profile and submits again."
        confirmText="Return"
        onConfirm={returnToStudent}
        busy={ev.busy === "return"}
      />
      <NoteModal
        show={modal === "void"}
        onHide={() => setModal(null)}
        title="Void this agreement"
        label="Why is it void?"
        hint="Kept on the record. A new evaluation can be started afterwards."
        confirmText="Void"
        variant="destructive"
        onConfirm={voidIt}
        busy={ev.busy === "void"}
      />
      <SignModal
        show={modal === "sign"}
        onHide={() => setModal(null)}
        onConfirm={sign}
        busy={ev.busy === "sign"}
        defaultName={evaluation.signatory?.name || me?.name}
        defaultTitle={evaluation.signatory?.title}
      />
      <AgreementPdfModal
        show={modal === "pdf"}
        onHide={() => setModal(null)}
        fetchBytes={fetchPdf}
        reloadKey={pdfKey}
        fileName={`${a.number || "agreement"}${status === "evaluated" ? "-draft" : ""}.pdf`}
        title={a.number ? `Agreement ${a.number}` : "Agreement draft"}
      />
    </div>
  );
};

export default EvaluationTab;
